import { Head, useForm } from '@inertiajs/react'
import { GraduationCap, Lock, Mail, User } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { FieldError } from '@/components/feedback/FieldError'

type SchoolInfo = {
  id: string
  name: string
}

export type SchoolCoachNewProps = {
  school: SchoolInfo
}

export default function SchoolCoachNew({ school }: SchoolCoachNewProps) {
  const { data, setData, post, processing, errors, transform } = useForm({
    email: '',
    name: '',
    password: '',
    password_confirmation: '',
  })

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ school_coach: current }))
    post(`/school/school_coaches?school_id=${school.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <AppShell>
      <Head title="コーチ登録" />
      <Card padding="lg" className="space-y-8">
        <header className="space-y-3 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-white/60">
            <GraduationCap className="h-5 w-5 text-brand-200" />
            <span>{school.name}</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">コーチアカウント登録</h1>
          <p className="text-sm text-white/60">
            登録後、チームとのチャットや選手検索機能をご利用いただけます。
          </p>
        </header>

        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="coach-email" className="flex items-center gap-2 text-white">
              <Mail className="h-4 w-4 text-brand-200" />
              メールアドレス
            </Label>
            <Input
              id="coach-email"
              type="email"
              value={data.email}
              onChange={(event) => setData('email', event.target.value)}
              placeholder="coach@school.jp"
              required
            />
            <FieldError message={errors.email} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="coach-name" className="flex items-center gap-2 text-white">
              <User className="h-4 w-4 text-brand-200" />
              氏名
            </Label>
            <Input
              id="coach-name"
              value={data.name}
              onChange={(event) => setData('name', event.target.value)}
              placeholder="山田 太郎"
              required
            />
            <FieldError message={errors.name} />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="coach-password" className="flex items-center gap-2 text-white">
                <Lock className="h-4 w-4 text-brand-200" />
                パスワード
              </Label>
              <Input
                id="coach-password"
                type="password"
                value={data.password}
                onChange={(event) => setData('password', event.target.value)}
                required
              />
              <FieldError message={errors.password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coach-password-confirm" className="flex items-center gap-2 text-white">
                <Lock className="h-4 w-4 text-brand-200" />
                パスワード（確認）
              </Label>
              <Input
                id="coach-password-confirm"
                type="password"
                value={data.password_confirmation}
                onChange={(event) => setData('password_confirmation', event.target.value)}
                required
              />
              <FieldError message={errors.password_confirmation} />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={processing}>
            アカウントを作成
          </Button>
        </form>

        <p className="text-center text-xs text-white/50">
          登録に問題がある場合は、運営までご連絡ください。
        </p>
      </Card>
    </AppShell>
  )
}
